import { useGetSharesForProjectNavigationQuery } from '@frameio/graphql-client/generated';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';
import { LoadingContainer, Text } from '@frameio/vapor';
import { useSharesFilterBy, useSharesSortBy } from '../../../../hooks';
import { Shares } from '../Shares';
import { CreateShareButton } from '../Shares/CreateShareButton';
import { SharesButton } from '../Shares/SharesButton';
import TEXT from './generated/TEXT/StaticShares';
import styles from './StaticProjectNav.module.css';

type Props = {
  projectId: string;
};

/**
 * StaticShares should only be mounted if the user has canShare permission.
 */
export function StaticShares({ projectId }: Props) {
  const [sortBy] = useSharesSortBy();
  const [filterBy] = useSharesFilterBy();
  const { data, previousData, loading, error } =
    useGetSharesForProjectNavigationQuery({
      variables: {
        projectId,
        page: { first: DEFAULT_INITIAL_PAGE_SIZE },
        orderBy: [{ direction: sortBy.order, field: sortBy.field }],
        filters: filterBy,
      },
      skip: !projectId,
    });

  const shares =
    data?.account?.project?.shares?.nodes ||
    previousData?.account?.project?.shares?.nodes;

  if (error) {
    throw new Error(error?.message ?? TEXT.errorLabel);
  }

  const isLoading = loading && !shares;

  return (
    <LoadingContainer
      className={styles.section}
      isLoading={isLoading}
      loadingLabel={TEXT.loadingShares}
    >
      <div className={styles.sectionTitle}>
        <Text
          className={styles.titleText}
          color="secondary"
          variant="secondary"
        >
          {TEXT.shareLinks}
        </Text>

        <div className={styles.rightContents}>
          <SharesButton buttonVariant="tertiary-dimmed" />
          <CreateShareButton
            buttonVariant="tertiary-dimmed"
            projectId={projectId}
          />
        </div>
      </div>

      <div className={styles.sharesList}>
        <Shares dndNamespace="shares-nav" shares={shares} />
      </div>
    </LoadingContainer>
  );
}

TEXT.DEFINE({
  /** Error message when share links fail to load */
  errorLabel: 'An error has occurred retrieving Share Links',
  /** Aria-label for shares section loading state */
  loadingShares: 'Loading shares',
  /** Section title for Share Links */
  shareLinks: 'Share Links',
});
